/**
 * prospectStatus — énum canonique de ProspectQueue.status (ADR-032).
 *
 * 3 valeurs :
 *  - active        : prospect dialable selon canCall() + dialIntent()
 *  - do_not_call   : DNC légal/explicite — jamais dialable
 *  - placeholder   : import incomplet — invisible
 *
 * "Quel badge afficher ?"      → dialIntent(prospect, lastCall) — dialRules.ts
 * "Cet appel est-il en cours ?" → activeCalls / prospectCallMap (DialerContext)
 * "Ai-je le droit d'appeler ?"  → canCall() — dialRules.ts
 *
 * Le champ `do_not_call: boolean` du schéma Mongoose duplique
 * `status === 'do_not_call'`. Conservé pour rétrocompat — privilégier
 * le status pour toute nouvelle logique.
 *
 * Miroir backend : server/src/domain/crm/entities/ProspectQueue.ts
 */

export type ProspectStatus = 'active' | 'do_not_call' | 'placeholder';

interface ProspectStatusMap {
  ACTIVE: ProspectStatus;
  DO_NOT_CALL: ProspectStatus;
  PLACEHOLDER: ProspectStatus;
  /** @deprecated use ACTIVE */
  PENDING: ProspectStatus;
  /** @deprecated use ACTIVE */
  CALLING: ProspectStatus;
  /** @deprecated use ACTIVE */
  CONTACTED: ProspectStatus;
}

export const PROSPECT_STATUS: ProspectStatusMap = {
  ACTIVE: 'active',
  DO_NOT_CALL: 'do_not_call',
  PLACEHOLDER: 'placeholder',
  // Aliases dépréciés (rétrocompat code, ADR-032 fenêtre de transition).
  // L'ancien code lisait PROSPECT_STATUS.PENDING/CALLING/CONTACTED.
  PENDING: 'active',
  CALLING: 'active',
  CONTACTED: 'active',
};

export const ALL_PROSPECT_STATUSES: ProspectStatus[] = ['active', 'do_not_call', 'placeholder'];

/**
 * Rétrocompat lecture (ADR-032 fenêtre de transition).
 * Mappe les anciennes valeurs (`pending`, `calling`, `contacted`) vers
 * `active`. À supprimer après 1 release stable + migration BDD confirmée.
 */
export function normalizeStatus(s: string | null | undefined): string {
  if (s === 'pending' || s === 'calling' || s === 'contacted' || s === 'new') {
    return PROSPECT_STATUS.ACTIVE;
  }
  return s ?? '';
}

/**
 * Filtre status uniquement (do_not_call, placeholder exclus). La règle complète
 * passe par canCall() dans dialRules.ts qui ajoute phone valide, source, session,
 * limite, et l'analyse du dernier outcome.
 */
export function isStatusDialable(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized !== PROSPECT_STATUS.DO_NOT_CALL && normalized !== PROSPECT_STATUS.PLACEHOLDER;
}

/**
 * Helper centralisé pour compter les prospects "dans la file" — alignement
 * de tous les badges de comptage (FloatingCallBar, OnCallStatusBar,
 * PowerHourBadge, sidebar stats) sur la même règle.
 * Inclut 'active' (canonique) + les anciens 'pending'/'calling'/'contacted'
 * pendant la fenêtre de transition (ADR-032).
 */
export function isQueuedStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return normalized === PROSPECT_STATUS.ACTIVE;
}
